import React, { useCallback, useMemo, useRef, useState } from 'react';
import qs from 'qs';
import { useRouteMatch, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Alert } from '@material-ui/lab';
import { useOperatorState } from '../../features/home/hooks/useOperatorState';
import { useBundleRebuild } from '../../features/home/components/Diagnostics/useBundleRebuild';
import { useDiagnosticsEvents } from '../../features/home/hooks/useDiagnosticsEvents';
import { useDiagnosticsSnapshot } from '../../features/home/hooks/useDiagnosticsSnapshot';
import { BridgeClient } from '../../app/service/bridge/BridgeClient';
import Diagnostics from '../../features/home/components/Diagnostics';
import DiagnosticsEventPanel from '../../features/home/components/DiagnosticsEventPanel';
import DiagnosticsSnapshotPanel from '../../features/home/components/DiagnosticsSnapshotPanel';
import SupportReportModal from '../../features/home/components/SupportReportModal';
import { useShellAppContext } from '../../features/shell/context/useShellAppContext';
import { loadShellSession } from '../../features/shell/session/shellSessionStorage';
import SearchFeaturePageShell from '../../components/shell/SearchFeaturePageShell';
import CloudDiagnosticsScreen from '../../features/home/CloudDiagnosticsScreen';
import styles from './DiagnosticsScreen.module.scss';
import { ADMIN_RUNTIME_COMMANDS } from '../../app/runtime/adminRuntime';
import {
  supportsMarketplacePluginBridgeShape,
  supportsPluginArtifactMutationSnapshot,
} from '../../features/shell/pluginCapabilities';
import IntlMessages from 'util/IntlMessages';

function PluginDiagnosticsScreen() {
  const match = useRouteMatch('/app/:shopId');
  const location = useLocation();
  const settingsShopId = useSelector((store) => store.settings?.shopId);
  const queryParams = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search],
  );
  const shopId = match?.params?.shopId || queryParams.shopId || settingsShopId;
  const { operatorState, isLoading, pluginSnapshot, browserLocalLane } = useOperatorState();
  const { status: bundleRebuildStatus, error: bundleRebuildError, startPreviewBuild, startBuildAndPromote } = useBundleRebuild();
  const bridgeRef = useRef(null);
  const isDiagnosticsLoading = (!pluginSnapshot && isLoading) || !shopId;

  const eventsEnabled = pluginSnapshot?.capabilities?.diagnosticEventsV1 === true;
  const snapshotEnabled = pluginSnapshot?.capabilities?.diagnosticSnapshotV1 === true;

  const eventsData = useDiagnosticsEvents({ enabled: eventsEnabled });
  const snapshotData = useDiagnosticsSnapshot({ enabled: snapshotEnabled });

  // Support modal
  const {
    gatewayToken,
    gatewayAccessToken,
    bridgeClient: shellBridgeClient,
    capabilities: shellCapabilities,
    runtime,
  } = useShellAppContext();
  const liveArtifactMutationCapability = supportsPluginArtifactMutationSnapshot(pluginSnapshot)
    || (
      shellCapabilities?.artifactMutationV1 === true
      && supportsMarketplacePluginBridgeShape(pluginSnapshot)
    );
  const artifactMutationSupported = (
    shellCapabilities?.artifactMutationV1 === true || shellCapabilities?.sharedAdminRuntimeV1 === true
  )
    && liveArtifactMutationCapability
    && runtime?.supports?.(ADMIN_RUNTIME_COMMANDS.BUNDLE_BUILD_AND_PROMOTE) === true;
  const gatewayBaseUrl = useMemo(() => loadShellSession()?.gatewayBaseUrl ?? null, []);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const handleOpenSupportModal = useCallback(() => setIsSupportModalOpen(true), []);
  const handleCloseSupportModal = useCallback(() => setIsSupportModalOpen(false), []);
  const supportBridgeClient = shellBridgeClient ?? (bridgeRef.current || (bridgeRef.current = new BridgeClient()));

  const handleStartReindex = useCallback(async () => {
    if (!bridgeRef.current) {
      bridgeRef.current = new BridgeClient();
    }
    await bridgeRef.current.sendMessageWithResponse('indexing.start', {});
  }, []);

  const handleRebuildBundle = useCallback(() => {
    startPreviewBuild(shopId);
  }, [startPreviewBuild, shopId]);

  const handleBuildAndPromote = useCallback(() => {
    startBuildAndPromote(shopId);
  }, [startBuildAndPromote, shopId]);

  return (
    <SearchFeaturePageShell
      pageId="diagnostics"
      description={<IntlMessages id="ui.diagnosticsScreen.diagnosticsScreen.checkSearchHealthUnderstandProblemsAndFollowSafeRecoverySteps" />}
      isLoading={isDiagnosticsLoading}
    >
      <div className={styles.page}>
        {!artifactMutationSupported && (
          <Alert severity="warning" data-testid="diagnostics-plugin-upgrade-required">
            <IntlMessages id="ui.diagnosticsScreen.diagnosticsScreen.updateToPrestaShopSearchPlugin108OrNewerToRebuild" />
          </Alert>
        )}
        <Diagnostics
          operatorState={operatorState}
          pluginSnapshot={pluginSnapshot}
          browserLocalLane={browserLocalLane}
          shopId={shopId}
          isLoading={false}
          onStartReindex={handleStartReindex}
          onRebuildBundle={artifactMutationSupported ? handleRebuildBundle : null}
          onBuildAndPromote={artifactMutationSupported ? handleBuildAndPromote : null}
          bundleRebuildStatus={bundleRebuildStatus}
          bundleRebuildError={bundleRebuildError}
        />
        {eventsEnabled && (
          <DiagnosticsEventPanel
            events={eventsData.events}
            hasMore={eventsData.hasMore}
            nextSinceId={eventsData.nextSinceId}
            isLoading={eventsData.isLoading}
            error={eventsData.error}
            onLoadMore={eventsData.loadMore}
            onSendToSupport={handleOpenSupportModal}
          />
        )}
        {snapshotEnabled && (
          <DiagnosticsSnapshotPanel
            snapshot={snapshotData.snapshot}
            capabilities={snapshotData.capabilities}
            pluginCapabilities={pluginSnapshot?.capabilities || {}}
            warnings={snapshotData.warnings}
            isLoading={snapshotData.isLoading}
            error={snapshotData.error}
          />
        )}
      </div>
      {eventsEnabled && (
        <SupportReportModal
          open={isSupportModalOpen}
          onClose={handleCloseSupportModal}
          bridgeClient={supportBridgeClient}
          baseUrl={gatewayBaseUrl}
          token={gatewayToken}
          authToken={gatewayAccessToken}
        />
      )}
    </SearchFeaturePageShell>
  );
}

export default function DiagnosticsScreen() {
  const { productProfile } = useShellAppContext();
  return productProfile?.id === 'cloud'
    ? <CloudDiagnosticsScreen />
    : <PluginDiagnosticsScreen />;
}
